import { AiOutlinePercentage } from 'react-icons/ai';
import { FiDownload } from 'react-icons/fi';
import FilterTable, { BaseData } from './Table/Table';
import { PaymentDTO, PaymentType } from '../models/payment';
import { parseFriendlyDateAndHour } from '../utils/dates';
import { formatCurrency } from '../utils/shared';
import Button from './atoms/Button';
import { useGetEventPaymentReportQuery, useGetEventPaymentReportXlsxMutation } from '../store';
import useGetParams from '../hooks/useGetParams';
import LoadingLottie from './atoms/LoadingLottie';
import EmptyTable from './Table/EmptyTable';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';

interface DetailsTableRow extends BaseData {
  date: string | JSX.Element;
  order: string | JSX.Element;
  processed_by: string | JSX.Element;
  // status: string | JSX.Element;
  items_count: number | JSX.Element;
  amount_paid: number | JSX.Element;
  subtotal: number | JSX.Element;
  discounts: number | JSX.Element;
  gross_tickets_amount: number | JSX.Element;
  details: string | JSX.Element;
  received_by: string | JSX.Element;
  mark_paid_by: string | JSX.Element;
  service_charge: number | JSX.Element;
  // service_charge_rate: number | JSX.Element;
  platform_payment_id: string | JSX.Element;
  avantti_comission_amount: number | JSX.Element;
  // avantti_comission_rate: number | JSX.Element;
  seller_comission_amount: number | JSX.Element;
  // seller_comission_rate: number | JSX.Element;
  referral_comission_amount: number | JSX.Element;
  // referral_comission_rate: number | JSX.Element;
  payment_method_discount_amount: number | JSX.Element;
  wires_tax_withheld_amount: number | JSX.Element;
  other_tax_withheld_amount: number | JSX.Element;
  net_producer_amount: number | JSX.Element;
  gateway_amount: number | JSX.Element;
  // avantti_comission_vat_amount: number | JSX.Element;
  gross_received_amount: number | JSX.Element;
  absorbed_service_charge_amount: number | JSX.Element;
}

const PaymentsDetailsTable = () => {
  const { event } = useGetSelectedEvent();
  const { eventId, businessId } = useGetParams();
  const [getEventPaymentReportXlsx, { isLoading: isLoadingXlsx }] = useGetEventPaymentReportXlsxMutation();
  const { data, isLoading } = useGetEventPaymentReportQuery({
    eventId,
    businessId,
  });

  if (isLoading) {
    return <LoadingLottie />;
  }

  if (!data) {
    return <div>No hay datos</div>;
  }

  const handleDownloadExport = () => {
    getEventPaymentReportXlsx({ eventId, businessId });
  };

  const getProcessedByLabel = (provider: string) => {
    switch (provider) {
      case 'MERCADOPAGO':
        return 'Mercado Pago';
      case 'AVANTTI':
        return 'Avantti';
      case 'UALA':
        return 'Uala';
      case 'PAYWAY':
        return 'Payway';
      default:
        return provider;
    }
  };

  const getReceivedByLabel = (received_by: string | null) => {
    switch (received_by) {
      case 'business':
        return 'Productora';
      case 'seller':
        return 'Vendedor';
      case 'avantti':
        return 'Avantti';
      case 'split':
        return 'Split';
      case 'none':
        return 'Gratuita';
      default:
        return received_by;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'approved':
        return 'Aprobado';
      case 'rejected':
        return 'Rechazado';
      case 'reimbursed':
        return 'Reembolsado';
      default:
        return status;
    }
  };

  const getMarkPaidByLabel = (payment: PaymentDTO) => {
    if (payment.mark_paid_by) {
      return payment.mark_paid_by_user?.username ?? payment.mark_paid_by;
    }
    return 'Cliente';
  };

  const detailsTableRows: DetailsTableRow[] | undefined = data
    .slice()
    .sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB.getTime() - dateA.getTime();
    })
    .map((payment) => ({
      id: payment.id,
      date: <div className='whitespace-nowrap'>{parseFriendlyDateAndHour(String(payment.created_at), true, true)}</div>,
      order: <div className='whitespace-nowrap'>{payment.order?.display_id}</div>,
      status: <div className='whitespace-nowrap'>{getStatusLabel(payment.status)}</div>,
      processed_by: <div className='whitespace-nowrap'>{getProcessedByLabel(payment.provider)}</div>,
      mark_paid_by: <div className='whitespace-nowrap'>{getMarkPaidByLabel(payment)}</div>,
      items_count: <div className='whitespace-nowrap text-right'>{payment.items_count ?? 0}</div>,
      gross_tickets_amount: <div className='whitespace-nowrap text-right'>{formatCurrency(payment.gross_tickets_amount ?? 0)}</div>,
      discounts: <div className='whitespace-nowrap text-right'>{formatCurrency(payment.discounts_amount ?? 0)}</div>,
      subtotal: <div className='whitespace-nowrap text-right'>{formatCurrency(payment.subtotal ?? 0)}</div>,
      // service_charge_rate: <div className='whitespace-nowrap text-right'>{(payment.service_charge_rate ?? 0) * 100}%</div>,
      service_charge: <div className='whitespace-nowrap text-right'>{formatCurrency(payment.service_charge_amount ?? 0)}</div>,
      amount_paid: <div className='whitespace-nowrap text-right'>{formatCurrency(payment.paid_amount ?? 0)}</div>,
      payment_method_discount_amount: <div className='whitespace-nowrap text-right'>{formatCurrency(payment.payment_method_discount_amount ?? 0)}</div>,
      // avantti_comission_rate: <div className='whitespace-nowrap text-right'>{(payment.avantti_comission_rate ?? 0) * 100}%</div>,
      avantti_comission_amount: <div className='whitespace-nowrap text-right'>{formatCurrency((payment.avantti_comission_amount || payment.online_comission_amount) ?? 0)}</div>,
      // avantti_comission_vat_amount: <div className='whitespace-nowrap text-right'>{formatCurrency(payment.avantti_comission_vat_amount ?? 0)}</div>,
      // seller_comission_rate: <div className='whitespace-nowrap text-right'>{(payment.seller_comission_rate ?? 0) * 100}%</div>,
      seller_comission_amount: <div className='whitespace-nowrap text-right'>{formatCurrency(payment.seller_comission_amount ?? 0)}</div>,
      // referral_comission_rate: <div className='whitespace-nowrap text-right'>{(payment.referral_comission_rate ?? 0) * 100}%</div>,
      referral_comission_amount: <div className='whitespace-nowrap text-right'>{formatCurrency(payment.referral_comission_amount ?? 0)}</div>,
      wires_tax_withheld_amount: <div className='whitespace-nowrap text-right'>{formatCurrency(payment.wires_tax_withheld_amount ?? 0)}</div>,
      other_tax_withheld_amount: <div className='whitespace-nowrap text-right'>{formatCurrency(payment.other_tax_withheld_amount ?? 0)}</div>,
      net_producer_amount: <div className='whitespace-nowrap text-right'>{formatCurrency(payment.net_producer_amount ?? 0)}</div>,
      received_by: <div className='whitespace-nowrap'>{getReceivedByLabel(payment.received_by)}</div>,
      platform_payment_id: <div className='whitespace-nowrap'>{payment.reference_number}</div>,
      gateway_amount: <div className='whitespace-nowrap text-right'>{formatCurrency(payment.gateway_amount ?? 0)}</div>,
      gross_received_amount: <div className='whitespace-nowrap text-right'>{formatCurrency(payment.gross_received_amount ?? 0)}</div>,
      details: <div className='whitespace-nowrap'>{payment.details}</div>,
      absorbed_service_charge_amount: <div className='whitespace-nowrap text-right'>{formatCurrency(payment.absorbed_service_charge_amount ?? 0)}</div>,
    }));

  // ineficiente
  const hasSplitPayments = data.some((payment) => payment.received_by === 'split');
  const hasManualPayments = data.some((payment) => payment.type === PaymentType.MANUAL);

  const detailsTableConfig = [
    {
      id: 'date',
      label: 'Fecha',
    },
    {
      id: 'order',
      label: 'ID Orden',
    },
    // { por ahora no tiene sentido: son todos aprobados
    //   id: 'status',
    //   label: 'Estado',
    // },
    {
      id: 'gross_tickets_amount',
      label: 'Monto entradas',
    },
    {
      id: 'discounts',
      label: 'Monto de descuentos',
    },
    {
      id: 'subtotal',
      label: 'Subtotal',
    },
    // {
    //   id: 'service_charge_rate',
    //   label: '% de service charge',
    // },
    {
      id: 'service_charge',
      label: 'Monto de service charge',
    },
    {
      id: 'amount_paid',
      label: 'Monto abonado',
    },
    {
      id: 'payment_method_discount_amount',
      label: 'Descuento por método de pago',
    },
    {
      id: 'items_count',
      label: 'Cantidad de entradas',
    },
    ...((hasManualPayments || hasSplitPayments) ? [{
      id: 'avantti_comission_amount',
      label: 'Comisión de Avantti',
    }] : []),
    ...(hasSplitPayments
      ? [
        {
          id: 'gateway_amount',
          label: 'Comisión de MercadoPago',
        },
        {
          id: 'wires_tax_withheld_amount',
          label: 'Ret. Imp. Cred. y Deb.',
        },
        {
          id: 'other_tax_withheld_amount',
          label: 'Otras Ret. de Imp.',
        },
        {
          id: 'gross_received_amount',
          label: 'Monto bruto recibido',
        },
      ]
      : []),
    {
      id: 'seller_comission_amount',
      label: 'Comisión del vendedor',
    },
    {
      id: 'referral_comission_amount',
      label: 'Comisión de referido',
    },
    ...(event?.absorbed_service_charge_rate && event.absorbed_service_charge_rate > 0 ? [{
      id: 'absorbed_service_charge_amount',
      label: 'Service Charge incluido',
    }] : []),
    {
      id: 'net_producer_amount',
      label: 'Monto neto',
    },
    {
      id: 'received_by',
      label: 'Dinero recibido por',
    },
    {
      id: 'platform_payment_id',
      label: 'ID Procesador',
    },
    {
      id: 'details',
      label: 'Detalles',
    },
  ];

  const customButton = <Button onClick={handleDownloadExport} variant='secondary' className='whitespace-nowrap' loading={isLoadingXlsx}>
    <div className='flex flex-row gap-2 items-center'>
      <FiDownload className='text-xl' />
      <p className='whitespace-nowrap hidden md:block'>Descargar listado</p>
    </div>
  </Button>;

  return (
    <div className="glass-surface">
      <h1 className="text-2xl font-medium mb-2">Detalles de pagos</h1>
      {detailsTableRows && detailsTableRows.length > 0 ? (
        <FilterTable
          rows={detailsTableRows}
          headCells={detailsTableConfig}
          customButtons={[customButton]}
          pagination={{ enabled: true, pageSize: 50 }}
          filterColumns
          tableId="payments-details-table"
          highlightedRows
        />
      ) : (
        <EmptyTable
          title='No hay pagos'
          info='No hay pagos para mostrar'
          icon={AiOutlinePercentage}
          iconColor='text-secondary-500'
        />
      )}
    </div>
  );
};

export default PaymentsDetailsTable;
